import type { FluxErrorEntityErrorCodes } from '../types.js';

export const RoleErrorCodes = {
  RoleAuthUserNoRole: {
    code: 'role/auth-user-no-role',
    explanation: 'O usuário autenticado não possui um papel (role) definido.',
    localizations: {},
    message: 'O usuário autenticado não possui um cargo definido.',
  },
  RoleContextConflict: {
    code: 'role/context-conflict',
    explanation:
      'O papel (role) fornecido tem um conflito de contexto. Certifique-se de que o papel seja apropriado para o escopo (conta, banco de dados, fluxo, etc.) em que está sendo atribuído. Verifique se o id da entidade relacionada está correto e que nenhum id conflitante está sendo usado.',
    localizations: {},
    message: 'O cargo informado possui um conflito de contexto.',
  },
  RoleDoesNotBelongToDatabase: {
    code: 'role/does-not-belong-to-database',
    explanation: 'O papel (role) fornecido não pertence ao banco de dados especificado.',
    localizations: {},
    message: 'O cargo informado não pertence ao banco de dados especificado.',
  },
  RoleGuestToPipe: {
    code: 'role/guest-to-pipe',
    explanation:
      'Um convidado não pode ser atribuído a um fluxo (pipe) com permissões superiores a "leitor".',
    localizations: {},
    message: 'Um convidado não pode ser atribuído a um fluxo com permissões acima de leitor.',
  },
  RoleInsufficientPermissions: {
    code: 'role/insufficient-permissions',
    explanation:
      'Você não tem permissão necessária para atribuir a este papel (role). Para atribuir uma permissão a um papel, você deve essa permissão.',
    localizations: {},
    message: 'Você não tem permissão para esta ação.',
    supportingDocs: [
      {
        name: 'Papéis',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  RoleInvalidBulkUsers: {
    code: 'role/invalid-bulk-users',
    explanation:
      'O número de usuários fornecido para a operação em massa de papéis (roles) é inválido. Deve ser uma lista válida de IDs de usuários com no mínimo um usuário.',
    localizations: {},
    message:
      'O número de usuários informados é inválido. Verifique suas informações e tente novamente.',
  },
  RoleInvalidType: {
    code: 'role/invalid-type',
    explanation:
      'O tipo de papel (role) fornecido é inválido. Verifique se o tipo está correto e existe para esse escopo.',
    localizations: {},
    message: 'O tipo de cargo informado não é válido.',
    supportingDocs: [
      {
        name: 'Papéis',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  RoleIsManaged: {
    code: 'role/is-managed',
    explanation:
      'Este papel (role) é gerenciado pelo sistema e não pode ser alterado. Alguns papéis padrão são protegidos.',
    localizations: {},
    message: 'Este cargo é gerenciado pelo sistema e não pode ser alterado.',
  },
  RoleLicenseRequired: {
    code: 'role/license-required',
    explanation:
      'Uma licença ativa é necessária para atribuir este papel (role) a um usuário. Alterações de papéis podem exigir licenças específicas dependendo das permissões envolvidas.',
    localizations: {},
    message: 'É necessária uma licença ativa para atribuir este cargo ao usuário.',
    supportingDocs: [
      {
        name: 'Licenças',
        path: '/wiki/docs/licencas/intro',
      },
      {
        name: 'Operador',
        path: '/wiki/docs/licencas/operador',
      },
    ],
  },
  RoleManageUserAboveHierarchy: {
    code: 'role/manage-user-above-hierarchy',
    explanation:
      'Você não pode gerenciar um usuário com um papel (role) igual ou superior ao seu na hierarquia.',
    localizations: {},
    message: 'Você não pode gerenciar um usuário com um cargo igual ou superior ao seu.',
  },
  RoleNotFound: {
    code: 'role/not-found',
    explanation: 'O papel (role) que você está tentando acessar não foi encontrado.',
    localizations: {},
    message: 'Cargo não encontrado.',
  },
  RoleOwnerRole: {
    code: 'role/owner-role',
    explanation:
      'Você não pode definir um usuário como proprietário. O papel de proprietário é exclusivo.',
    localizations: {},
    message: 'Não é possível definir um usuário como proprietário.',
  },
  RoleSetAboveHierarchy: {
    code: 'role/set-above-hierarchy',
    explanation:
      'Você não pode atribuir um papel (role) igual ou superior ao seu na hierarquia. Apenas usuários com cargos superiores podem atribuir este nível de permissão. ',
    localizations: {},
    message: 'Você não pode atribuir um cargo igual ou superior ao seu na hierarquia.',
    supportingDocs: [
      {
        name: 'Papéis',
        path: '/wiki/docs/roles-and-permissions/roles',
      },
    ],
  },
  RoleUserAlreadyInRole: {
    code: 'role/user-already-in-role',
    explanation: 'O usuário já possui este papel (role).',
    localizations: {},
    message: 'O usuário já possui este cargo.',
  },
  RoleUserNoRole: {
    code: 'role/user-no-role',
    explanation: 'O usuário não possui um papel (role) atribuído.',
    localizations: {},
    message: 'O usuário não possui um cargo atribuído.',
  },
} as const satisfies FluxErrorEntityErrorCodes<'role'>;
